/* ProductReviews - Compact reviews summary and list for the product variant page
 * Displays average star rating, total count, and individual review cards.
 * Updated: Paginated with "load more" button — shows 6 at a time, summary reflects true total.
 */

import { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';

const PAGE_SIZE = 6;

interface Review {
  id: string;
  name: string;
  text: string;
  stars: number;
  created_at: string;
}

export default function ProductReviews() {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const { data, error, count } = await supabase
          .from('reviews')
          .select('id, name, text, stars, created_at', { count: 'exact' })
          .eq('is_published', true)
          .order('created_at', { ascending: false })
          .range(0, PAGE_SIZE - 1);

        if (error) throw error;
        setReviews(data || []);
        setTotalCount(count ?? (data?.length ?? 0));
      } catch (error) {
        console.error('Error fetching reviews:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchReviews();
  }, []);

  const loadMore = async () => {
    setIsLoadingMore(true);
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('id, name, text, stars, created_at')
        .eq('is_published', true)
        .order('created_at', { ascending: false })
        .range(reviews.length, reviews.length + PAGE_SIZE - 1);

      if (error) throw error;
      if (data) {
        setReviews((prev) => [...prev, ...data]);
      }
    } catch (error) {
      console.error('Error loading more reviews:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  if (isLoading) {
    return (
      <div className="py-12 text-center text-sm text-[#5a6552]">Reviews laden...</div>
    );
  }

  if (reviews.length === 0) return null;

  const avgRating = reviews.reduce((sum, r) => sum + r.stars, 0) / reviews.length;
  const hasMore = totalCount > reviews.length;

  const renderStars = (count: number, size: string = 'w-4 h-4') => (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`${size} ${i < count ? 'fill-amber-400 text-amber-400' : 'fill-[#e7e1d6] text-[#e7e1d6]'}`}
        />
      ))}
    </div>
  );

  return (
    <div>
      {/* Summary */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="text-5xl font-semibold tracking-[-0.02em] text-[#1e2b22]">
            {avgRating.toFixed(1)}
          </div>
          <div>
            {renderStars(Math.round(avgRating), 'w-5 h-5')}
            <p className="mt-1 text-sm text-[#5a6552]">Gebaseerd op {totalCount} reviews</p>
          </div>
        </div>
      </div>

      {/* Review cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {reviews.map((review, index) => (
          <motion.div
            key={review.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.3) }}
            viewport={{ once: true }}
            className="border-l-2 border-[#e7e1d6] pl-5"
          >
            <div className="mb-3 flex items-center justify-between">
              {renderStars(review.stars)}
              <span className="text-xs text-[#cfcac2]">
                {new Date(review.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            </div>
            <p className="text-sm leading-7 text-[#5a6552] mb-4">{review.text}</p>
            <p className="text-sm font-semibold text-[#1e2b22]">{review.name}</p>
          </motion.div>
        ))}
      </div>

      {/* Load more */}
      {hasMore && (
        <div className="mt-8 text-center">
          <button
            onClick={loadMore}
            disabled={isLoadingMore}
            className="inline-flex items-center gap-2 rounded-full border border-[#e7e1d6] bg-white px-6 py-3 text-sm font-medium text-[#1e2b22] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#f2ede4] disabled:opacity-50 disabled:hover:translate-y-0"
          >
            {isLoadingMore ? 'Laden...' : `Laat meer reviews zien (${totalCount - reviews.length})`}
          </button>
        </div>
      )}
    </div>
  );
}
